import React, { useState, useEffect } from 'react';
import {
  maxLength,
  minLength,
  SelectInput,
  required,
  TextInput,
  FormWithRedirect,
  Button,
} from 'react-admin';
import {
  makeStyles,
  Grid,
  Paper,
  Typography,
  Tooltip,
} from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import EditIcon from '@material-ui/icons/Edit';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import dataProvider from '../../../dataProvider';
import { perPage } from '../../../helper';

const useStyles = makeStyles(() => ({
  paperBase: {
    padding: '40px 30px',
    margin: '15px 0',
  },
  clickable: {
    cursor: 'pointer',
  },
  formRoot: {
    '& .MuiTextField-root': {
      width: '100%',
    },
    '& .MuiFilledInput-root': {
      backgroundColor: '#fdfdfd',
    },
  },
  pLR10: {
    padding: '0 10px',
  },
  center: {
    textAlign: 'center',
  },
}));

const twoColumnProps = {
  item: true,
  xs: 12,
  sm: 6,
};

const validateInternalName = [required(), minLength(2), maxLength(63)];
const validateMessage = [maxLength(1023)];

const CustomMessagesForm = ({
  record,
  newCustomMessages,
  setNewCustomMessages,
  editedCustomMessages,
  setEditedCustomMessages,
  selectedMessage1,
  setSelectedMessage1,
  selectedMessage2,
  setSelectedMessage2,
  customMessagesList,
  setCustomMessagesList,
}) => {
  const classes = useStyles();
  const [isMessage1FormOpen, setIsMessage1FormOpen] = useState(false);
  const [isMessage2FormOpen, setIsMessage2FormOpen] = useState(false);
  const [showMessage2Input, setShowMessage2Input] = useState(false);

  useEffect(() => {
    dataProvider
      .getList('service-custom-message', {
        filter: {},
        sort: {},
        pagination: { page: 1, perPage },
      })
      .then((res) =>
        // The first option (after the blank) in customMessagesList is the option to 'Create new'
        setCustomMessagesList([...customMessagesList, ...res.data]),
      );
  }, []);

  const handleSelect = (e, messageNumber) => {
    if (!e.target.value) {
      if (messageNumber === 1) setSelectedMessage1(null);
      else if (messageNumber === 2) setSelectedMessage2(null);
      return;
    }
    const selected = customMessagesList.find((el) => el.id === e.target.value);
    if (messageNumber === 1) {
      setSelectedMessage1(selected);
      if (selected.id === 'new') {
        setIsMessage1FormOpen(true);
      }
    } else if (messageNumber === 2) {
      setSelectedMessage2(selected);
      if (selected.id === 'new') {
        setIsMessage2FormOpen(true);
      }
    }
  };

  const handleCancel = (messageNumber) => {
    if (messageNumber === 1) {
      setIsMessage1FormOpen(false);
      setSelectedMessage1(record.custom_message_1 || null);
    } else if (messageNumber === 2) {
      setIsMessage2FormOpen(false);
      setSelectedMessage2(record.custom_message_2 || null);
      setShowMessage2Input(false);
    }
    const updatedNewCustomMessages = newCustomMessages;
    const updatedEditedCustomMessages = editedCustomMessages;
    delete updatedNewCustomMessages[messageNumber];
    delete updatedEditedCustomMessages[messageNumber];
    setNewCustomMessages(updatedNewCustomMessages);
    setEditedCustomMessages(updatedEditedCustomMessages);
  };

  const handleChange = (messageNumber, e, field) => {
    const selectedMessage =
      messageNumber === 1 ? selectedMessage1 : selectedMessage2;
    if (selectedMessage.id === 'new') {
      setNewCustomMessages({
        ...newCustomMessages,
        [messageNumber]: {
          ...newCustomMessages[messageNumber],
          [field]: e.target.value,
        },
      });
    } else {
      setEditedCustomMessages({
        ...editedCustomMessages,
        [messageNumber]: {
          ...selectedMessage,
          ...editedCustomMessages[messageNumber],
          [field]: e.target.value,
        },
      });
    }
  };

  const customMessageForm = (messageNumber) => {
    const selectedMessage =
      messageNumber === 1 ? selectedMessage1 : selectedMessage2;
    return (
      <FormWithRedirect
        resource="service-custom-message"
        render={() => (
          <Grid
            {...twoColumnProps}
            className={`${classes.pLR10} ${classes.center}`}
          >
            <TextInput
              resource="service-custom-message"
              validate={validateInternalName}
              source="internal_name"
              defaultValue={
                selectedMessage.id === 'new'
                  ? ''
                  : selectedMessage.internal_name
              }
              onChange={(e) => handleChange(messageNumber, e, 'internal_name')}
            />
            <Tooltip
              title="Appears on checkout page as superscript on this service"
              placement="bottom"
            >
              <TextInput
                resource="service-custom-message"
                multiline
                validate={validateMessage}
                source="booking_review_message"
                defaultValue={selectedMessage.booking_review_message}
                onChange={(e) =>
                  handleChange(messageNumber, e, 'booking_review_message')
                }
              />
            </Tooltip>
            <Tooltip
              title="Appears in SMS and Email communications with this service"
              placement="bottom"
            >
              <TextInput
                resource="service-custom-message"
                multiline
                validate={validateMessage}
                source="logistics_message"
                defaultValue={selectedMessage.logistics_message}
                onChange={(e) =>
                  handleChange(messageNumber, e, 'logistics_message')
                }
              />
            </Tooltip>
            <Button label="Cancel" onClick={() => handleCancel(messageNumber)}>
              <ClearIcon />
            </Button>
          </Grid>
        )}
      />
    );
  };

  const renderMessage2 = () => {
    if (isMessage2FormOpen) {
      return customMessageForm(2);
    }
    if (selectedMessage2 || showMessage2Input) {
      return (
        <Grid {...twoColumnProps} className={classes.pLR10}>
          <SelectInput
            resource="service-offering"
            source="custom_message_id_2"
            choices={customMessagesList}
            optionText="internal_name"
            allowEmpty
            onChange={(e) => handleSelect(e, 2)}
            value={selectedMessage2?.id || ''}
          />
          {selectedMessage2?.id && (
            <Grid item className={classes.center}>
              <Button label="Edit" onClick={() => setIsMessage2FormOpen(true)}>
                <EditIcon />
              </Button>
            </Grid>
          )}
        </Grid>
      );
    }
    if (selectedMessage1?.internal_name) {
      return (
        <Grid {...twoColumnProps} className={classes.center}>
          <Button label="Add" onClick={() => setShowMessage2Input(true)}>
            <AddCircleOutlineIcon />
          </Button>
        </Grid>
      );
    }
    return null;
  };

  return (
    <Paper className={classes.paperBase}>
      <Typography variant="h5" gutterBottom>
        Service-Specific Messages
      </Typography>
      {((isMessage1FormOpen && selectedMessage1.id !== 'new') ||
        (isMessage2FormOpen && selectedMessage2.id !== 'new')) && (
        <Typography className={classes.pLR10} gutterBottom>
          <i>
            Note: Editing an existing message will update the message for all
            service offerings using it.
          </i>
        </Typography>
      )}
      <Grid container className={classes.formRoot}>
        {isMessage1FormOpen ? (
          customMessageForm(1)
        ) : (
          <Grid {...twoColumnProps} className={classes.pLR10}>
            <SelectInput
              resource="service-offering"
              source="custom_message_id_1"
              choices={customMessagesList}
              optionText="internal_name"
              allowEmpty
              onChange={(e) => handleSelect(e, 1)}
              value={selectedMessage1?.id || ''}
            />
            {selectedMessage1?.id && (
              <Grid item className={classes.center}>
                <Button
                  label="Edit"
                  onClick={() => setIsMessage1FormOpen(true)}
                >
                  <EditIcon />
                </Button>
              </Grid>
            )}
          </Grid>
        )}
        {renderMessage2()}
      </Grid>
    </Paper>
  );
};

export default CustomMessagesForm;
